import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicFormComponent } from 'src/app/shared/components/dynamic-form/dynamic-form.component';
import { GoogleRecaptchaService } from 'src/app/shared/services/google-recaptcha/google-recaptcha.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { SessionMgmtService } from 'src/app/shared/services/session-mgmt/session-mgmt.service';
import {
  DEFAULT_PROJECT_TIMEZONE,
  PROJECT_NAME_ALLOWED_PATTERN,
  PROJECT_NAME_DUPLICATE_MESSAGE,
  PROJECT_NAME_FORBIDDEN_MESSAGE,
  PROJECT_NAME_MAX_LENGTH_MESSAGE,
  PROJECT_NAME_MIN_LENGTH_MESSAGE,
  PROJECT_NAME_REQUIRED_MESSAGE,
  loadTimezoneOptions,
  getProjectTypeFromForm,
  normalizeCountrySelection,
  normalizeProjectName,
  toCountryOptions,
  timezoneObjectFromName,
  validateProjectNameLive
} from '../project-form.util';

@Component({
  selector: 'app-create-blank-project',
  templateUrl: './create-blank-project.component.html',
  styleUrls: ['./create-blank-project.component.scss'],
  standalone: true,
  imports: [CommonModule, DynamicFormComponent]
})
export class CreateBlankProjectComponent implements OnInit {
  @Output() projectCreated = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @ViewChild(DynamicFormComponent) dynamicForm?: DynamicFormComponent;

  formSchema: any = null;
  isSubmitting: boolean = false;
  recaptchaToken: string | null = null;
  timezones: any[] = [];
  timezoneObjects: any[] = [];
  countries: any[] = [];
  existingProjectNames: string[] = [];

  constructor(
    private googleRecaptcha: GoogleRecaptchaService,
    private toast: ToastService,
    private accountsService: AccountsService,
    private appStateService: AppStateService,
    private helpersService: HelpersService,
    private sessionMgmt: SessionMgmtService
  ) {}

  ngOnInit(): void {
    const timezoneData = loadTimezoneOptions();
    this.timezones = timezoneData.names;
    this.timezoneObjects = timezoneData.objects;
    this.countries = toCountryOptions();
    this.loadExistingProjects();
    this.buildFormSchema();
    this.initializeRecaptcha();
  }

  private loadExistingProjects(): void {
    const accountDetails: any = this.appStateService.accountDetails();
    const accountId = accountDetails?._id || localStorage.getItem('LtMsAcTiDfR');
    if (!accountId) {
      return;
    }

    this.accountsService.getBrandsList(accountId).subscribe({
      next: (response) => {
        if (response && response.data && response.data.brands) {
          this.existingProjectNames = response.data.brands.map((brand: any) => brand.name);
          this.dynamicForm?.form?.get('project_name')?.updateValueAndValidity();
        }
      },
      error: () => {
        this.toast.showPopup('ERROR', 'Error', 'We are facing problem while connecting to the server. Please check your internet connection or try again after sometime.', 1500);
      }
    });
  }

  private buildFormSchema(): void {
    this.formSchema = {
      type: 'object',
      properties: {
        project_name: {
          label: 'Project Name',
          type: 'string',
          minLength: 3,
          maxLength: 50,
          placeholder: 'New Project Name',
          pattern: PROJECT_NAME_ALLOWED_PATTERN,
          customValidation: (value: string) => validateProjectNameLive(value, this.existingProjectNames),
          errorMessages: {
            required: PROJECT_NAME_REQUIRED_MESSAGE,
            minLength: PROJECT_NAME_MIN_LENGTH_MESSAGE,
            maxLength: PROJECT_NAME_MAX_LENGTH_MESSAGE,
            pattern: PROJECT_NAME_FORBIDDEN_MESSAGE,
            custom: PROJECT_NAME_DUPLICATE_MESSAGE
          }
        },
        audience_type: {
          label: 'Audience',
          type: 'string',
          widget: 'segmented',
          enum: ['Customer', 'Employee'],
          default: 'Customer',
          activeColor: '#66b846',
          errorMessages: {
            required: 'Please select an audience type'
          }
        },
        timezone: {
          label: 'Time Zone',
          type: 'string',
          input: 'dropdown',
          options: this.timezones,
          optionLabel: 'label',
          optionValue: 'value',
          placeholder: 'Select Time Zone',
          default: DEFAULT_PROJECT_TIMEZONE.name,
          errorMessages: {
            required: 'Time zone is required'
          }
        },
        is_hierarchy_search_enabled: {
          label: 'Enable filter across hierarchy',
          type: 'boolean',
          widget: 'checkbox',
          default: false,
          description: 'Indicates if hierarchy search enabled for this project'
        },
        audience_countries: {
          label: 'Audience Countries',
          type: 'array',
          input: 'multi-select',
          widget: 'multi-select',
          options: this.countries,
          optionLabel: 'label',
          optionValue: 'value',
          placeholder: 'Select a country',
          default: ['India'],
          description: 'This field indicates one or multiple audience countries for which this project is created'
        }
      },
      required: ['project_name', 'audience_type', 'timezone']
    };
  }

  private initializeRecaptcha(): void {
    this.googleRecaptcha.initializeToken().subscribe(token => {
      this.recaptchaToken = token;
    });
  }

  onFormSubmit(formValue: any): void {
    if (this.isSubmitting) {
      return;
    }

    const projectName = normalizeProjectName(formValue.project_name);
    const nameCheck = validateProjectNameLive(projectName, this.existingProjectNames);
    if (!projectName || !nameCheck.valid) {
      this.toast.showPopup(
        'ERROR',
        'Invalid Project Name',
        nameCheck.message || (projectName ? PROJECT_NAME_DUPLICATE_MESSAGE : PROJECT_NAME_REQUIRED_MESSAGE),
        3000
      );
      return;
    }

    const accountDetails: any = this.appStateService.accountDetails();
    const accountId = accountDetails?._id || localStorage.getItem('LtMsAcTiDfR');
    if (!accountId) {
      this.toast.showPopup('ERROR', 'Account Error', 'Account ID not found. Please refresh the page and try again.', 3000);
      return;
    }

    this.submitProject(formValue, accountId, projectName);
  }

  private submitProject(formValue: any, accountId: string, projectName: string): void {
    this.isSubmitting = true;
    this.googleRecaptcha.initializeToken().subscribe(token => {
      if (!token) {
        this.isSubmitting = false;
        this.toast.showPopup('ERROR', 'reCAPTCHA Required', 'Please complete the reCAPTCHA verification.', 3000);
        return;
      }
      this.recaptchaToken = token;
      this.createProject(formValue, accountId, projectName);
    });
  }

  private createProject(formValue: any, accountId: string, projectName: string): void {
    const userName = this.getLoggedInUserName();
    const projectTimezone = timezoneObjectFromName(
      formValue.timezone || formValue.time_zone,
      this.timezoneObjects
    );
    const audienceCountries = normalizeCountrySelection(
      formValue.audience_countries || formValue.audience_country
    );

    const projectData: any = {
      brand_name: projectName,
      timezone: projectTimezone,
      g_recaptcha_response_v3: this.recaptchaToken,
      // g_recaptcha_response: 'litmusworld_internal',
      project_type: getProjectTypeFromForm(formValue),
      dashboard_version: 3,
      is_single_touchpoint_setup: true,
      account_id: accountId
    };

    if (userName) {
      projectData.user_name = userName;
    }
    if (audienceCountries.length) {
      projectData.audience_countries = audienceCountries;
    }
    if (formValue.is_hierarchy_search_enabled || formValue.enable_filter_across_hierarchy) {
      projectData.is_hierarchy_search_enabled = true;
    }

    this.accountsService.createBrand(projectData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        const payload = response?.data || response;
        const code = payload?.code ?? response?.code;

        if (code === 0 || code === 401 || code === 406) {
          this.toast.showPopup('SUCCESS', 'Success', 'New project has been created successfully', 3000);
          if (payload?.is_tp_created === false || payload?.is_tp_created === 'false') {
            this.toast.showPopup('ERROR', 'Error', 'Default Touchpoint could not be created at the moment. Please try again later.', 3000);
          }
          this.projectCreated.emit({
            brand_id: payload?.brand_id,
            brand_name: projectData.brand_name,
            ...projectData
          });
          this.recaptchaToken = null;
          this.initializeRecaptcha();
        } else {
          this.toast.showPopup('ERROR', 'Error', payload?.error_message || payload?.message || 'Something went wrong', 3000);
          this.recaptchaToken = null;
          this.initializeRecaptcha();
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        const errorCode = error?.error?.data?.code;
        let errorMessage = 'Failed to create project. Please try again.';
        if (errorCode === 4052) {
          errorMessage = 'Error updating account for the created brand. Please contact your administrator.';
        } else if (errorCode === 4002) {
          errorMessage = 'Account details not found. Please refresh and try again.';
        } else if (errorCode === 4053) {
          errorMessage = 'Authorization failed. Please login again.';
        } else if (error?.error?.data?.error_message || error?.error?.message) {
          errorMessage = error.error.data?.error_message || error.error.message;
        }
        this.toast.showPopup('ERROR', 'Error', errorMessage, 5000);
        this.recaptchaToken = null;
        this.initializeRecaptcha();
      }
    });
  }

  private getLoggedInUserName(): string {
    const helpers = this.helpersService.helpers;
    const fromHelpers =
      helpers?.managerDetails?.manager?.user_name ||
      helpers?.managerDetails?.loggedInManager?.user_name;
    if (fromHelpers) {
      return fromHelpers;
    }

    const session = this.sessionMgmt.getData('LOGIN_RESPONSE');
    if (!session) {
      return '';
    }

    let parsed = session;
    if (typeof session === 'string') {
      try {
        parsed = JSON.parse(session);
      } catch {
        return '';
      }
    }

    return parsed?.manager?.user_name || parsed?.loggedInManager?.user_name || '';
  }

  submit(): void {
    this.dynamicForm?.onSubmit();
  }

  canSubmit(): boolean {
    return !this.isSubmitting && !!this.dynamicForm?.isFormReady();
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
